import { useCallback, useMemo, useState } from 'react';
import type {
  FeatureFlagClientConfiguration, FeatureFlagClientConfigurationMap,
  FeatureFlagDrawerMode, FeatureFlagFormFieldErrors, FeatureFlagMetadataField,
  FeatureFlagMetadataFormValues, FeatureFlagPendingConfirmation,
  FeatureFlagTableRow, FeatureFlagTableSortDirection, FeatureFlagTableSortKey,
  FeatureFlagTargetClient,
} from '../../../types/features/featureFlags';
import { createEmptyFeatureClientConfigurations } from '../constants/featureFlagOptions';
import { useFeatureFlagsList } from './useFeatureFlagsList';
import { useFeatureFlagStoreOptions } from './useFeatureFlagStoreOptions';
import { useFeatureFlagsUrlFilters } from './useFeatureFlagsUrlFilters';
import { useFeatureFlagAssignmentControls } from './useFeatureFlagAssignmentControls';
import { useFeatureFlagAssignedIdsLoader } from './useFeatureFlagAssignedIdsLoader';
import { useFeatureFlagSubmitHandlers } from './useFeatureFlagSubmitHandlers';
import {
  EMPTY_FEATURE_METADATA_VALUES,
  getFeatureFlagConfirmationDialogContent,
  getSelectedFeatureFlagTargetClient,
} from '../utils/featureFlagsPageControlUtils';

export const useFeatureFlagsPageControls = () => {
  const [activeTargetClient, setActiveTargetClient] = useState<FeatureFlagTargetClient>('webapp');
  const [clientConfigurations, setClientConfigurations] = useState<FeatureFlagClientConfigurationMap>(
    createEmptyFeatureClientConfigurations,
  );
  const [drawerMode, setDrawerMode] = useState<FeatureFlagDrawerMode>('create');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingFeatureRow, setEditingFeatureRow] = useState<FeatureFlagTableRow | null>(null);
  const [featureFieldErrors, setFeatureFieldErrors] = useState<FeatureFlagFormFieldErrors>({});
  const [featureFormError, setFeatureFormError] = useState('');
  const [featureMetadataValues, setFeatureMetadataValues] = useState<FeatureFlagMetadataFormValues>(
    EMPTY_FEATURE_METADATA_VALUES,
  );
  const [featureSubmitting, setFeatureSubmitting] = useState(false);
  const [pendingConfirmation, setPendingConfirmation] = useState<FeatureFlagPendingConfirmation | null>(null);
  const [storeAssignmentsChanged, setStoreAssignmentsChanged] = useState(false);
  const [tableSortDirection, setTableSortDirection] = useState<FeatureFlagTableSortDirection>('desc');
  const [tableSortKey] = useState<FeatureFlagTableSortKey>('assignmentCount');
  const [userAssignmentsChanged, setUserAssignmentsChanged] = useState(false);
  const {
    clientFilter,
    handleClientFilterChange,
    handleSearchChange,
    search,
    setClientFilter,
  } = useFeatureFlagsUrlFilters();
  const {
    featureRows,
    featureRowsError,
    featureRowsLoading,
    handleRefreshFeatureRows,
  } = useFeatureFlagsList(clientFilter, search, tableSortDirection);

  const activeClientConfiguration = clientConfigurations[activeTargetClient];
  const storeOptionsEnabled = (
    drawerOpen
    && activeClientConfiguration.selected
    && !activeClientConfiguration.globalEnabled
    && activeClientConfiguration.assignmentTarget === 'store_wise'
  );

  const {
    error: storeOptionsError,
    handleResetFilters: handleResetStoreFilters,
    handleSearchChange: handleStoreSearchChange,
    handleStatusFilterChange: handleStoreStatusFilterChange,
    loading: storeOptionsLoading,
    search: storeSearch,
    statusFilter: storeStatusFilter,
    stores: filteredStoreOptions,
  } = useFeatureFlagStoreOptions(storeOptionsEnabled);

  const selectedTargetClient = getSelectedFeatureFlagTargetClient(clientConfigurations);
  const {
    assignedIdsError,
    assignedIdsLoading,
    handleLoadAssignedStoreIds,
    handleLoadAssignedUserIds,
    resetAssignedIdsState,
  } = useFeatureFlagAssignedIdsLoader({
    setClientConfigurations,
    setFeatureFormError,
    setStoreAssignmentsChanged,
    setUserAssignmentsChanged,
  });

  const {
    handleAssignmentTargetChange,
    handleBulkStoreAssignmentChange,
    handleBulkUserAssignmentChange,
    handleStoreAssignmentChange,
    handleUserAssignmentChange,
  } = useFeatureFlagAssignmentControls({
    activeTargetClient,
    setClientConfigurations,
    setFeatureFieldErrors,
    setFeatureFormError,
    setStoreAssignmentsChanged,
    setUserAssignmentsChanged,
  });

  const confirmationDialogContent = useMemo(() => (
    getFeatureFlagConfirmationDialogContent(pendingConfirmation)
  ), [pendingConfirmation]);

  const handleOpenCreateDrawer = useCallback(() => {
    resetAssignedIdsState();
    setActiveTargetClient(clientFilter);
    setClientConfigurations(createEmptyFeatureClientConfigurations(clientFilter));
    setDrawerMode('create');
    setEditingFeatureRow(null);
    setFeatureFieldErrors({});
    setFeatureFormError('');
    setFeatureMetadataValues(EMPTY_FEATURE_METADATA_VALUES);
    handleResetStoreFilters();
    setStoreAssignmentsChanged(false);
    setUserAssignmentsChanged(false);
    setDrawerOpen(true);
  }, [clientFilter, handleResetStoreFilters, resetAssignedIdsState]);

  const handleOpenUpdateDrawer = useCallback((row: FeatureFlagTableRow) => {
    resetAssignedIdsState();
    const nextConfigurations = createEmptyFeatureClientConfigurations(row.client);
    const assignmentTarget = row.rolloutTarget === 'users' ? 'user_wise' : 'store_wise';

    setActiveTargetClient(row.client);
    setEditingFeatureRow(row);
    setClientConfigurations({
      ...nextConfigurations,
      [row.client]: {
        ...nextConfigurations[row.client],
        assignmentTarget,
        globalEnabled: row.globalEnabled,
        selected: true,
        storeIds: [],
        userIds: [],
      },
    });
    setDrawerMode('update');
    setFeatureFieldErrors({});
    setFeatureFormError('');
    setFeatureMetadataValues({
      description: row.description,
      displayName: row.displayName,
      featureKey: row.featureKey,
    });
    handleResetStoreFilters();
    setStoreAssignmentsChanged(false);
    setUserAssignmentsChanged(false);
    setDrawerOpen(true);

    if (row.rolloutTarget === 'stores') {
      void handleLoadAssignedStoreIds(row);
    } else if (row.rolloutTarget === 'users') {
      void handleLoadAssignedUserIds(row);
    }
  }, [handleLoadAssignedStoreIds, handleLoadAssignedUserIds, handleResetStoreFilters, resetAssignedIdsState]);

  const handleCloseDrawer = useCallback(() => {
    if (featureSubmitting) {
      return;
    }

    resetAssignedIdsState();
    setDrawerOpen(false);
    setEditingFeatureRow(null);
    setFeatureFieldErrors({});
    setFeatureFormError('');
    setStoreAssignmentsChanged(false);
    setUserAssignmentsChanged(false);
  }, [featureSubmitting, resetAssignedIdsState]);

  const handleFeatureMetadataFieldChange = useCallback((field: FeatureFlagMetadataField, value: string) => {
    setFeatureFormError('');
    setFeatureFieldErrors((currentErrors) => ({
      ...currentErrors,
      [field]: undefined,
    }));
    setFeatureMetadataValues((currentValues) => ({
      ...currentValues,
      [field]: value,
    }));
  }, []);

  const handleActiveTargetClientChange = useCallback((client: FeatureFlagTargetClient) => {
    setActiveTargetClient(client);
  }, []);

  const handleTargetClientSelectedChange = useCallback((client: FeatureFlagTargetClient, selected: boolean) => {
    setFeatureFormError('');
    setFeatureFieldErrors((currentErrors) => ({
      ...currentErrors,
      targetClient: undefined,
    }));
    setActiveTargetClient(client);
    setClientConfigurations((currentConfigurations) => ({
      mobile_app: {
        ...currentConfigurations.mobile_app,
        selected: client === 'mobile_app' ? selected : false,
      },
      webapp: {
        ...currentConfigurations.webapp,
        selected: client === 'webapp' ? selected : false,
      },
    }));
  }, []);

  const handleClientConfigurationChange = useCallback((
    client: FeatureFlagTargetClient,
    updates: Partial<FeatureFlagClientConfiguration>,
  ) => {
    setFeatureFormError('');
    setFeatureFieldErrors((currentErrors) => ({
      ...currentErrors,
      storeIds: updates.globalEnabled === true ? undefined : currentErrors.storeIds,
      userIds: updates.globalEnabled === true ? undefined : currentErrors.userIds,
    }));
    setClientConfigurations((currentConfigurations) => ({
      ...currentConfigurations,
      [client]: {
        ...currentConfigurations[client],
        ...updates,
      },
    }));
  }, []);

  const handleGlobalEnabledChange = useCallback((globalEnabled: boolean) => {
    if (globalEnabled === activeClientConfiguration.globalEnabled) {
      return;
    }

    setPendingConfirmation({
      client: activeTargetClient,
      type: globalEnabled ? 'global_on' : 'global_off',
    });
  }, [activeClientConfiguration.globalEnabled, activeTargetClient]);

  const handleConfirmationCancel = useCallback(() => {
    setPendingConfirmation(null);
  }, []);

  const handleConfirmationConfirm = useCallback(() => {
    if (!pendingConfirmation) {
      return;
    }

    handleClientConfigurationChange(pendingConfirmation.client, {
      globalEnabled: pendingConfirmation.type === 'global_on',
    });

    setPendingConfirmation(null);
  }, [handleClientConfigurationChange, pendingConfirmation]);

  const {
    handleCreateFeature,
    handleSaveFeature,
    handleUpdateFeature,
  } = useFeatureFlagSubmitHandlers({
    activeClientConfiguration,
    clientConfigurations,
    clientFilter,
    drawerMode,
    editingFeatureRow,
    featureMetadataValues,
    handleRefreshFeatureRows,
    handleResetStoreFilters,
    selectedTargetClient,
    setActiveTargetClient,
    setClientConfigurations,
    setClientFilter,
    setDrawerMode,
    setDrawerOpen,
    setEditingFeatureRow,
    setFeatureFieldErrors,
    setFeatureFormError,
    setFeatureMetadataValues,
    setFeatureSubmitting,
    setStoreAssignmentsChanged,
    setUserAssignmentsChanged,
    storeAssignmentsChanged,
    userAssignmentsChanged,
  });

  const handleTableSort = useCallback((key: FeatureFlagTableSortKey) => {
    if (key !== 'assignmentCount') {
      return;
    }

    setTableSortDirection((currentDirection) => (currentDirection === 'asc' ? 'desc' : 'asc'));
  }, []);

  return {
    activeClientConfiguration,
    activeTargetClient,
    assignmentIdsLoading: assignedIdsLoading,
    clientFilter,
    clientConfigurations,
    confirmationDialogContent,
    drawerMode,
    drawerOpen,
    featureFormError,
    featureFieldErrors,
    featureMetadataValues,
    featureRows,
    featureRowsError,
    featureRowsLoading,
    featureSubmitting,
    filteredStoreOptions,
    handleActiveTargetClientChange,
    handleAssignmentTargetChange,
    handleBulkStoreAssignmentChange,
    handleBulkUserAssignmentChange,
    handleClientFilterChange,
    handleCloseDrawer,
    handleConfirmationCancel,
    handleConfirmationConfirm,
    handleCreateFeature,
    handleFeatureMetadataFieldChange,
    handleGlobalEnabledChange,
    handleOpenCreateDrawer,
    handleOpenUpdateDrawer,
    handleSaveFeature,
    handleSearchChange,
    handleStoreAssignmentChange,
    handleStoreSearchChange,
    handleStoreStatusFilterChange,
    handleTableSort,
    handleTargetClientSelectedChange,
    handleUserAssignmentChange,
    search,
    storeOptionsError: assignedIdsError || storeOptionsError,
    storeOptionsLoading,
    storeSearch,
    storeStatusFilter,
    tableSortDirection,
    tableSortKey,
  };
};
